import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export type ModuleType = 'core' | 'construction' | 'billing' | 'crm' | null;

interface ModuleSession {
  module: ModuleType;
  userEmail: string | null;
  userId: string | null;
  tenantId: string | null;
  isActive: boolean;
  loginTime: string | null;
}

interface ModuleSessionContextType {
  // Core session
  coreSession: ModuleSession;
  setCoreSession: (email: string) => void;
  clearCoreSession: () => void;
  
  // Construction session
  constructionSession: ModuleSession;
  setConstructionSession: (email: string, userId?: string, tenantId?: string) => void;
  clearConstructionSession: () => void;

  // Billing session
  billingSession: ModuleSession;
  setBillingSession: (email: string) => void;
  clearBillingSession: () => void;

  // CRM session
  crmSession: ModuleSession;
  setCrmSession: (email: string, userId?: string, tenantId?: string) => void;
  clearCrmSession: () => void;
  
  // Current module based on route
  currentModule: ModuleType;
  
  // Utilities
  isLoggedInToModule: (module: ModuleType) => boolean;
  getActiveSession: () => ModuleSession | null;
}

const ModuleSessionContext = createContext<ModuleSessionContextType | undefined>(undefined);

const CORE_SESSION_KEY = 'mls_core_session';
const CONSTRUCTION_SESSION_KEY = 'mls_construction_session';
const BILLING_SESSION_KEY = 'mls_billing_session';
const CRM_SESSION_KEY = 'mls_crm_session';

/** localStorage per sessioni modulo: sopravvive al reload della preview/dev server */
const MODULE_SESSION_STORAGE = localStorage;

const createEmptySession = (module: ModuleType): ModuleSession => ({
  module,
  userEmail: null,
  userId: null,
  tenantId: null,
  isActive: false,
  loginTime: null,
});

const loadSessionFromStorage = (key: string, module: ModuleType): ModuleSession => {
  try {
    let stored = MODULE_SESSION_STORAGE.getItem(key);
    // Migrazione da sessionStorage (vecchia implementazione)
    if (!stored) {
      const legacy = sessionStorage.getItem(key);
      if (legacy) {
        MODULE_SESSION_STORAGE.setItem(key, legacy);
        sessionStorage.removeItem(key);
        stored = legacy;
      }
    }
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...createEmptySession(module), ...parsed, module };
    }
  } catch (e) {
    console.error(`Failed to load session ${key}:`, e);
  }
  return createEmptySession(module);
};

const saveSessionToStorage = (key: string, session: ModuleSession) => {
  try {
    MODULE_SESSION_STORAGE.setItem(key, JSON.stringify(session));
  } catch (e) {
    console.error(`Failed to save session ${key}:`, e);
  }
};

const clearSessionFromStorage = (key: string) => {
  try {
    MODULE_SESSION_STORAGE.removeItem(key);
  } catch (e) {
    console.error(`Failed to clear session ${key}:`, e);
  }
};

export function ModuleSessionProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  const [coreSession, setCoreSessionState] = useState<ModuleSession>(() => 
    loadSessionFromStorage(CORE_SESSION_KEY, 'core')
  );
  
  const [constructionSession, setConstructionSessionState] = useState<ModuleSession>(() => 
    loadSessionFromStorage(CONSTRUCTION_SESSION_KEY, 'construction')
  );

  const [billingSession, setBillingSessionState] = useState<ModuleSession>(() => 
    loadSessionFromStorage(BILLING_SESSION_KEY, 'billing')
  );

  const [crmSession, setCrmSessionState] = useState<ModuleSession>(() => 
    loadSessionFromStorage(CRM_SESSION_KEY, 'crm')
  );

  // Determine current module based on route
  // /settings* è superficie Core (module explorer / suite settings)
  const currentModule: ModuleType = location.pathname.startsWith('/construction') 
    ? 'construction' 
    : location.pathname.startsWith('/billing')
      ? 'billing'
      : location.pathname.startsWith('/crm')
        ? 'crm'
        : location.pathname.startsWith('/core')
          || location.pathname.startsWith('/platform-admin')
          || location.pathname.startsWith('/settings')
          ? 'core'
          : null;

  const setCoreSession = useCallback((email: string) => {
    const newSession: ModuleSession = {
      module: 'core',
      userEmail: email,
      userId: null,
      tenantId: null,
      isActive: true,
      loginTime: new Date().toISOString(),
    };
    setCoreSessionState(newSession);
    saveSessionToStorage(CORE_SESSION_KEY, newSession);
  }, []);

  const clearCoreSession = useCallback(() => {
    const emptySession = createEmptySession('core');
    setCoreSessionState(emptySession);
    clearSessionFromStorage(CORE_SESSION_KEY);
  }, []);

  const setConstructionSession = useCallback((email: string, userId?: string, tenantId?: string) => {
    const newSession: ModuleSession = {
      module: 'construction',
      userEmail: email,
      userId: userId || null,
      tenantId: tenantId || null,
      isActive: true,
      loginTime: new Date().toISOString(),
    };
    setConstructionSessionState(newSession);
    saveSessionToStorage(CONSTRUCTION_SESSION_KEY, newSession);
  }, []);

  const clearConstructionSession = useCallback(() => {
    const emptySession = createEmptySession('construction');
    setConstructionSessionState(emptySession);
    clearSessionFromStorage(CONSTRUCTION_SESSION_KEY);
  }, []);

  const setBillingSession = useCallback((email: string) => {
    const newSession: ModuleSession = {
      module: 'billing',
      userEmail: email,
      userId: null,
      tenantId: null,
      isActive: true,
      loginTime: new Date().toISOString(),
    };
    setBillingSessionState(newSession);
    saveSessionToStorage(BILLING_SESSION_KEY, newSession);
  }, []);

  const clearBillingSession = useCallback(() => {
    const emptySession = createEmptySession('billing');
    setBillingSessionState(emptySession);
    clearSessionFromStorage(BILLING_SESSION_KEY);
  }, []);

  const setCrmSession = useCallback((email: string, userId?: string, tenantId?: string) => {
    const newSession: ModuleSession = {
      module: 'crm',
      userEmail: email,
      userId: userId || null,
      tenantId: tenantId || null,
      isActive: true,
      loginTime: new Date().toISOString(),
    };
    setCrmSessionState(newSession);
    saveSessionToStorage(CRM_SESSION_KEY, newSession);
  }, []);

  const clearCrmSession = useCallback(() => {
    const emptySession = createEmptySession('crm');
    setCrmSessionState(emptySession);
    clearSessionFromStorage(CRM_SESSION_KEY);
  }, []);

  const isLoggedInToModule = useCallback((module: ModuleType): boolean => {
    if (module === 'core') return coreSession.isActive;
    if (module === 'construction') return constructionSession.isActive;
    if (module === 'billing') return billingSession.isActive;
    if (module === 'crm') return crmSession.isActive;
    return false;
  }, [coreSession.isActive, constructionSession.isActive, billingSession.isActive, crmSession.isActive]);

  const getActiveSession = useCallback((): ModuleSession | null => {
    if (currentModule === 'core' && coreSession.isActive) return coreSession;
    if (currentModule === 'construction' && constructionSession.isActive) return constructionSession;
    if (currentModule === 'billing' && billingSession.isActive) return billingSession;
    if (currentModule === 'crm' && crmSession.isActive) return crmSession;
    return null;
  }, [currentModule, coreSession, constructionSession, billingSession, crmSession]);

  return (
    <ModuleSessionContext.Provider
      value={{
        coreSession,
        setCoreSession,
        clearCoreSession,
        constructionSession,
        setConstructionSession,
        clearConstructionSession,
        billingSession,
        setBillingSession,
        clearBillingSession,
        crmSession,
        setCrmSession,
        clearCrmSession,
        currentModule,
        isLoggedInToModule,
        getActiveSession,
      }}
    >
      {children}
    </ModuleSessionContext.Provider>
  );
}

export function useModuleSession() {
  const context = useContext(ModuleSessionContext);
  if (context === undefined) {
    throw new Error('useModuleSession must be used within a ModuleSessionProvider');
  }
  return context;
}
